import { useEffect, useRef, useState } from "react";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useInvoiceApi } from "@/hooks/apiHooks/useInvoiceApi";
import type { OrderInvoiceSummary } from "@models/order";
import { saveBlob } from "@utils/fileDownload";
import { invoiceDownloadErrorMessage } from "./_helpers/invoiceDownloadError";

interface InvoiceDownloadProps {
    // The order's invoice as the order read exposes it; `null` = the API says
    // no invoice was issued.
    invoice: OrderInvoiceSummary | null;
    // Who is looking, for the `null` case. The admin IS support, so they read
    // that no invoice has been issued and can act on it — with no link, since
    // the public support page would eject them from the admin shell. The
    // customer sees nothing: an ACH order's invoice follows settlement, so
    // null at confirmation is a transient state, not an escalation, and the
    // invoice also arrives by email.
    variant: "admin" | "customer";
}

// The inline failure is keyed by the invoice it belongs to: the admin order
// route re-uses this instance across orders, so a sentence stored for one
// order must not render beside another's button.
interface InlineError {
    invoiceId: string;
    message: string;
}

// "Download Invoice" for an order (E7 #214/#216), shared by the admin order
// detail and the customer confirmation. The PDF endpoint needs the bearer
// token, so the bytes are fetched through the hook and saved client-side. A
// voided invoice (#217) is still downloadable; its VOIDED mark is the separate
// InvoiceVoidedBadge, which the order pages render beside this.
// No invoice (`null`, none issued) renders a sentence for the admin only,
// nothing for the customer. A failed download always renders a sentence — the
// contracted 403/404s their own, everything else one generic line — never
// nothing.
export const InvoiceDownload = ({ invoice, variant }: InvoiceDownloadProps) => {
    const { downloadInvoicePdf } = useInvoiceApi();

    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<InlineError | null>(null);

    // A download the user navigated away from must neither save a file onto
    // the page they are now on nor update an unmounted component.
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    // `!invoice`, not `=== null`: the field ships, but every other wire field
    // that could lag a deploy is read defensively here, and an absent one
    // must not reach `invoice.voidedAt` and take the page to GlobalError.
    if (!invoice) {
        return variant === "admin" ? (
            <p className="text-sm text-neutral/70">
                No invoice has been issued for this order.
            </p>
        ) : null;
    }

    const handleDownload = async () => {
        setIsDownloading(true);
        setError(null);

        try {
            const { blob, fileName } = await downloadInvoicePdf(invoice.id);

            if (!isMounted.current) {
                return;
            }

            // The server's Content-Disposition name wins; the fallback
            // rebuilds the same shape from what we hold — `<number>.pdf`,
            // `-VOID` when voided — so the badge's promise holds either way.
            saveBlob(
                blob,
                fileName ??
                    `${invoice.invoiceNumber}${invoice.voidedAt ? "-VOID" : ""}.pdf`,
            );
        } catch (caught) {
            if (!isMounted.current) {
                return;
            }

            // Contracted 403/404 outcomes get their own sentence, everything
            // else the generic one — never nothing, or a failed download would
            // look like a saved file. useApi's toast also fires for the
            // failures it handles; that is fine (the cert-review helper does
            // the same).
            setError({
                invoiceId: invoice.id,
                message: invoiceDownloadErrorMessage(caught),
            });
        } finally {
            if (isMounted.current) {
                setIsDownloading(false);
            }
        }
    };

    const inlineError =
        error?.invoiceId === invoice.id ? error.message : null;

    return (
        <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    color={ComponentColor.Primary}
                    size={ComponentSize.SM}
                    outline
                    loading={isDownloading}
                    onClick={() => void handleDownload()}
                >
                    Download Invoice
                </Button>
            </div>
            {inlineError && (
                <p role="alert" className="text-sm text-error">
                    {inlineError}
                </p>
            )}
        </div>
    );
};
